import * as React from 'react';

import { cn } from '@/lib/utils';

// =============================================================================
// detail-ui.tsx — Habillage partagé des panneaux du détail campagne (refonte 2026-09-16).
// =============================================================================
// Cartes de la colonne droite (libellé mono 11px uppercase, parité volet Focus du tableau de bord), bouton
// noir, formatage des identifiants. Présentation uniquement : aucune règle métier.

/** Libellé de carte (mono 11px uppercase, parité volet Focus du tableau de bord). */
export const BLOCK_LABEL =
  'font-mono text-[11px] font-medium uppercase tracking-wider text-muted-foreground';

/** Bouton noir (parité « Ouvrir » actif du tableau de bord). */
export const DARK_BUTTON =
  'border-foreground bg-foreground text-background hover:bg-foreground/90 hover:text-background';

/** Bouton texte discret (« Effacer », « Retirer » dans une carte). */
export const TEXT_BUTTON =
  'rounded-sm text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50';

/** Compteur / mention mono d'un titre de Panel (le Panel ne porte qu'un compteur numérique). */
export function TitleMono({
  children,
}: {
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <span className="font-mono text-sm font-normal text-muted-foreground">
      {children}
    </span>
  );
}

/** SIREN 9 chiffres → « 572 015 246 » ; autre format inchangé. */
export function formatSiren(siren: string): string {
  return /^\d{9}$/.test(siren)
    ? `${siren.slice(0, 3)} ${siren.slice(3, 6)} ${siren.slice(6)}`
    : siren;
}

/**
 * Carte de la colonne droite : <section> nommée par son libellé mono (h2), actions optionnelles à droite.
 * `tone="danger"` : bordure destructive (zone irréversible).
 */
export function SideCard({
  headingId,
  label,
  actions,
  tone = 'default',
  children,
  className,
}: {
  readonly headingId: string;
  readonly label: React.ReactNode;
  readonly actions?: React.ReactNode;
  readonly tone?: 'default' | 'danger';
  readonly children: React.ReactNode;
  readonly className?: string;
}): React.JSX.Element {
  return (
    <section
      aria-labelledby={headingId}
      className={cn(
        'rounded-lg border bg-card',
        tone === 'danger' ? 'border-destructive/40' : 'border-border',
        className,
      )}
    >
      <div className="flex min-h-14 items-center justify-between gap-3 px-5 pt-4">
        <h2
          id={headingId}
          className={cn(BLOCK_LABEL, tone === 'danger' ? 'text-destructive' : undefined)}
        >
          {label}
        </h2>
        {actions}
      </div>
      <div className="space-y-4 px-5 pb-5 pt-3">{children}</div>
    </section>
  );
}

/** Pluriel français simple (0 et 1 au singulier). */
export function plural(count: number, singular: string, pluralForm?: string): string {
  return `${String(count)} ${count > 1 ? (pluralForm ?? `${singular}s`) : singular}`;
}
